import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';

import css from './MainPanelHeader.module.css';

/**
 * MainPanelHeader component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {React.Node} props.children - The children
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.isSortByActive - Whether the sort by is active
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The results count
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {React.Node} [props.resultsMessage] - Optional custom results message
 * @param {React.Node} props.noResultsInfo - The no results info
 * @param {string} [props.categoryHeading] - The category heading to display above the search results
 * @param {string} [props.categoryDescription] - Optional description shown under category heading
 * @param {string} [props.categoryIntro] - Optional intro text shown under description
 * @param {React.Node} [props.ctaContent] - Optional CTA content shown next to sort control
 * @param {React.Node} [props.headingCtas] - Optional CTA content shown next to heading
 * @returns {JSX.Element}
 */
const MainPanelHeader = props => {
  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    isSortByActive,
    listingsAreLoaded,
    resultsCount,
    searchInProgress = false,
    resultsMessage,
    noResultsInfo,
    categoryHeading,
    categoryDescription,
    categoryIntro,
    categoryIconUrl,
    ctaContent,
    headingCtas,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const defaultResultsMessage = searchInProgress ? (
    <FormattedMessage id="MainPanelHeader.loadingResults" />
  ) : (
    <FormattedMessage id="MainPanelHeader.foundResults" values={{ count: resultsCount }} />
  );
  const resolvedResultsMessage = searchInProgress ? defaultResultsMessage : resultsMessage || defaultResultsMessage;

  const showInlineResultsWithSort = categoryHeading && !ctaContent && isSortByActive;

  return (
    <div className={classes}>
      <div className={css.searchOptions}>
        {categoryHeading ? (
          <div className={css.categoryHeadingWrapper}>
            <div className={css.categoryHeadingRow}>
              {categoryIconUrl ? (
                <img className={css.categoryIcon} src={categoryIconUrl} alt="" />
              ) : null}
              <h1 className={css.categoryHeading}>{categoryHeading}</h1>
              {headingCtas ? <div className={css.categoryHeadingCtas}>{headingCtas}</div> : null}
            </div>
            {categoryDescription ? (
              <p className={css.categoryDescription}>{categoryDescription}</p>
            ) : null}
            {categoryIntro ? (
              <p className={css.categoryIntro}>{categoryIntro}</p>
            ) : null}
            {!ctaContent && !showInlineResultsWithSort ? (
              <span className={css.resultsCount}>{resolvedResultsMessage}</span>
            ) : null}
            {showInlineResultsWithSort ? (
              <div className={css.resultsSortRow}>
                <span className={css.resultsCountInline}>{resolvedResultsMessage}</span>
                <div className={css.sortInline}>{sortByComponent}</div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className={css.searchResultSummary}>
            <span className={css.resultsFound}>{resolvedResultsMessage}</span>
          </div>
        )}
        {ctaContent ? <div className={css.ctaInline}>{ctaContent}</div> : null}
        {isSortByActive && !showInlineResultsWithSort ? (
          <div className={css.sortyByWrapper}>
            {ctaContent && categoryHeading ? (
              <div className={css.resultsCountBelow}>{resolvedResultsMessage}</div>
            ) : null}
            {sortByComponent}
          </div>
        ) : null}
      </div>

      {children}

      {noResultsInfo ? noResultsInfo : null}
    </div>
  );
};

export default MainPanelHeader;
